import { useState, useEffect } from "react";
import { toast } from "sonner";
import axios from "axios";
import { AdminDashboard } from "@/components/admin/AdminDashboard";
import { AdminBlog } from "@/components/admin/AdminBlog";
import { AdminContacts } from "@/components/admin/AdminContacts";
import { AdminSettings } from "@/components/admin/AdminSettings";
import { AdminSocial } from "@/components/admin/AdminSocial";
import { AdminNavBuilder } from "@/components/admin/AdminNavBuilder";
import { AdminMedia } from "@/components/admin/AdminMedia";
import { AdminPageEditor } from "@/components/admin/AdminPageEditor";
import { AdminSubscribers } from "@/components/admin/AdminSubscribers";

const API = process.env.REACT_APP_BACKEND_URL;

const sidebarItems = [
  { id: "dashboard", label: "Dashboard", icon: "fa-tachometer-alt" },
  { id: "pages", label: "Page Editor", icon: "fa-file-alt" },
  { id: "blog", label: "Blog Posts", icon: "fa-pen-fancy" },
  { id: "navigation", label: "Navigation", icon: "fa-sitemap" },
  { id: "social", label: "Social Media", icon: "fa-share-alt" },
  { id: "media", label: "Media Library", icon: "fa-photo-video" },
  { id: "contacts", label: "Contacts", icon: "fa-address-book" },
  { id: "subscribers", label: "Mailing List", icon: "fa-envelope-open-text" },
  { id: "settings", label: "Settings", icon: "fa-cog" },
];

/* ─── Login Screen ───────────────────────────────────────── */
const AdminLogin = ({ onLogin }) => {
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/login`, { password: pw });
      if (data.success) {
        localStorage.setItem("paradigm_admin", "true");
        localStorage.setItem("paradigm_admin_ts", Date.now().toString());
        onLogin();
        toast.success("Welcome to the Admin Dashboard");
      }
    } catch {
      toast.error("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #1F2937 0%, #111827 100%)" }}>
      <div style={{ maxWidth: 420, width: "100%", padding: "2.5rem", background: "#fff", borderRadius: 16, boxShadow: "0 25px 50px rgba(0,0,0,.25)" }}>
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <div style={{ width: 56, height: 56, borderRadius: 12, background: "linear-gradient(135deg, #EC4899, #F97316)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1rem", fontSize: "1.5rem", color: "#fff" }}>
            <i className="fas fa-lock" />
          </div>
          <h2 style={{ fontSize: "1.5rem", fontWeight: 700, color: "#1F2937", margin: "0 0 .3rem" }}>Admin Dashboard</h2>
          <p style={{ color: "#6B7280", fontSize: ".875rem", margin: 0 }}>Paradigm Global Business Solutions</p>
        </div>
        <form onSubmit={handleSubmit} data-testid="admin-login-form">
          <div style={{ marginBottom: "1.25rem" }}>
            <label style={{ display: "block", fontSize: ".875rem", fontWeight: 600, color: "#374151", marginBottom: ".4rem" }}>Password</label>
            <div style={{ position: "relative" }}>
              <input
                type={showPw ? "text" : "password"}
                value={pw}
                onChange={e => setPw(e.target.value)}
                required
                placeholder="Enter admin password"
                data-testid="admin-password-input"
                style={{ width: "100%", padding: ".7rem 2.5rem .7rem .9rem", border: "1px solid #D1D5DB", borderRadius: 8, fontSize: ".9rem", outline: "none", transition: "border .2s" }}
                onFocus={e => e.target.style.borderColor = "#EC4899"}
                onBlur={e => e.target.style.borderColor = "#D1D5DB"}
              />
              <button type="button" onClick={() => setShowPw(!showPw)} style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "#9CA3AF", cursor: "pointer" }}>
                <i className={`fas ${showPw ? "fa-eye-slash" : "fa-eye"}`} />
              </button>
            </div>
          </div>
          <button type="submit" disabled={loading} data-testid="admin-login-button" style={{
            width: "100%", padding: ".75rem", border: "none", borderRadius: 8, background: "linear-gradient(135deg, #EC4899, #F97316)", color: "#fff", fontWeight: 700, fontSize: ".95rem", cursor: "pointer", transition: "opacity .2s"
          }}>
            {loading ? <><i className="fas fa-spinner fa-spin" /> Signing in...</> : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
};

/* ─── Main Admin Layout ─────────────────────────────────── */
export const AdminPage = () => {
  const [authed, setAuthed] = useState(() => {
    const isAuth = localStorage.getItem("paradigm_admin") === "true";
    const ts = parseInt(localStorage.getItem("paradigm_admin_ts") || "0");
    const dayMs = 24 * 60 * 60 * 1000;
    if (isAuth && Date.now() - ts > 30 * dayMs) {
      localStorage.removeItem("paradigm_admin");
      return false;
    }
    return isAuth;
  });
  const [tab, setTab] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const logout = () => {
    localStorage.removeItem("paradigm_admin");
    localStorage.removeItem("paradigm_admin_ts");
    setAuthed(false);
  };

  if (!authed) return <AdminLogin onLogin={() => setAuthed(true)} />;

  const currentItem = sidebarItems.find(i => i.id === tab);

  const renderPanel = () => {
    switch (tab) {
      case "dashboard": return <AdminDashboard onNavigate={setTab} />;
      case "pages": return <AdminPageEditor />;
      case "blog": return <AdminBlog />;
      case "navigation": return <AdminNavBuilder />;
      case "social": return <AdminSocial />;
      case "media": return <AdminMedia />;
      case "contacts": return <AdminContacts />;
      case "subscribers": return <AdminSubscribers />;
      case "settings": return <AdminSettings />;
      default: return <AdminDashboard onNavigate={setTab} />;
    }
  };

  return (
    <div data-testid="admin-dashboard" style={{ display: "flex", minHeight: "100vh", background: "#F3F4F6" }}>
      {/* Sidebar */}
      <aside style={{
        width: sidebarOpen ? 240 : 0, background: "#1F2937", color: "#fff",
        display: "flex", flexDirection: "column", position: "fixed", top: 0, bottom: 0, left: 0, zIndex: 100,
        transition: "width .2s", overflow: "hidden"
      }}>
        <div style={{ padding: "1.25rem 1rem", borderBottom: "1px solid rgba(255,255,255,.1)", display: "flex", alignItems: "center", gap: ".6rem" }}>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: "linear-gradient(135deg, #EC4899, #F97316)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: ".85rem", color: "#fff", flexShrink: 0 }}>
            <i className="fas fa-th-large" />
          </div>
          <div>
            <div style={{ fontSize: ".85rem", fontWeight: 700, lineHeight: 1.2 }}>Paradigm</div>
            <div style={{ fontSize: ".65rem", color: "rgba(255,255,255,.5)" }}>Admin Dashboard</div>
          </div>
        </div>

        <nav style={{ flex: 1, padding: ".5rem 0", overflowY: "auto" }}>
          {sidebarItems.map(item => (
            <button
              key={item.id}
              onClick={() => setTab(item.id)}
              data-testid={`admin-tab-${item.id}`}
              style={{
                display: "flex", alignItems: "center", gap: ".7rem", width: "100%",
                padding: ".65rem 1rem", margin: "2px .5rem", borderRadius: 8,
                border: "none", cursor: "pointer", fontSize: ".85rem", fontFamily: "Inter, sans-serif", textAlign: "left",
                background: tab === item.id ? "linear-gradient(135deg, rgba(236,72,153,.2), rgba(249,115,22,.15))" : "transparent",
                color: tab === item.id ? "#F9A8D4" : "rgba(255,255,255,.6)",
                fontWeight: tab === item.id ? 600 : 400,
                transition: "all .15s",
              }}
            >
              <i className={`fas ${item.icon}`} style={{ width: 18, textAlign: "center", fontSize: ".8rem" }} />
              {item.label}
            </button>
          ))}
        </nav>

        <div style={{ padding: ".75rem 1rem", borderTop: "1px solid rgba(255,255,255,.1)" }}>
          <button onClick={logout} data-testid="admin-logout-button" style={{
            display: "flex", alignItems: "center", gap: ".5rem", width: "100%", padding: ".55rem .8rem",
            background: "rgba(239,68,68,.1)", border: "1px solid rgba(239,68,68,.2)", borderRadius: 8,
            color: "#FCA5A5", cursor: "pointer", fontSize: ".8rem", fontFamily: "Inter, sans-serif",
          }}>
            <i className="fas fa-sign-out-alt" /> Logout
          </button>
        </div>
      </aside>

      {/* Main Area */}
      <div style={{ marginLeft: sidebarOpen ? 240 : 0, flex: 1, transition: "margin .2s", display: "flex", flexDirection: "column" }}>
        {/* Topbar */}
        <header style={{
          height: 64, background: "#fff", borderBottom: "1px solid #E5E7EB",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "0 1.5rem", position: "sticky", top: 0, zIndex: 50
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background: "none", border: "none", cursor: "pointer", color: "#6B7280", fontSize: "1.1rem" }}>
              <i className="fas fa-bars" />
            </button>
            <div>
              <h1 style={{ fontSize: "1.1rem", fontWeight: 700, color: "#1F2937", margin: 0, fontFamily: "Inter, sans-serif" }}>
                {currentItem?.label || "Dashboard"}
              </h1>
              <p style={{ fontSize: ".75rem", color: "#9CA3AF", margin: 0 }}>
                Admin / {currentItem?.label || "Dashboard"}
              </p>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: ".75rem" }}>
            <a href="/" target="_blank" rel="noopener noreferrer" style={{
              display: "flex", alignItems: "center", gap: ".4rem", padding: ".45rem .9rem", borderRadius: 8,
              border: "1px solid #E5E7EB", color: "#6B7280", fontSize: ".8rem", fontWeight: 500, textDecoration: "none",
            }}>
              <i className="fas fa-external-link-alt" style={{ fontSize: ".7rem" }} /> View Site
            </a>
          </div>
        </header>

        {/* Content */}
        <main style={{ flex: 1, padding: "1.5rem", overflowY: "auto" }}>
          {renderPanel()}
        </main>
      </div>
    </div>
  );
};

export default AdminPage;
